/**
 * useTodayCalendar — hook wrapper for the unified Appointments landing's
 * Today tab. Exposes `{ appointments, loading, error, reload }`.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { fetchTodayCalendar } from '../services/graphCalendarClient';
import type { Appointment } from '../types';

export interface UseTodayCalendarResult {
  appointments: ReadonlyArray<Appointment>;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useTodayCalendar(): UseTodayCalendarResult {
  const { user, token } = useAuth();
  const [appointments, setAppointments] = useState<ReadonlyArray<Appointment>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const branchCode = user?.activeBranchCode ?? user?.branchCode;

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const opts: { token?: string | null; branchCode?: string } = {};
      if (token !== undefined) opts.token = token;
      if (branchCode !== undefined) opts.branchCode = branchCode;
      const rows = await fetchTodayCalendar(opts);
      setAppointments(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token, branchCode]);

  useEffect(() => {
    void load();
  }, [load]);

  return { appointments, loading, error, reload: load };
}
